import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmploymentData, useEmployee } from '~/data/employee';
import { useEmployeeEmploymentData } from '~/data/employee/useEmployeeEmploymentData';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { filterActiveEnums } from '~/utils/helper';

const dotFormat = scheduledDateFormats.dotFormat;
const dashFormat = scheduledDateFormats.dashFormat;

export type UseEmploymentOutput = {
  terminationReasons: Enum[];
  fixedTermReasons: Enum[];
  trialPeriodOptions: Enum[];
  employmentTypeOptions: Enum[];
  employmentData: EmploymentData;
  loading: boolean;
  mapEmploymentPostData: (
    data: EmploymentFormValues,
    pending: boolean,
  ) => ScheduleEmploymentPostData;
  hasWorkEmail: boolean;
};

export type EmploymentFormValues = {
  fEmplTrialPeriod: string | null;
  fEmplTrialPeriodEndDate: moment.Moment | null;
  fEmplPlannedEndDate: moment.Moment | null;
  fEmplLastDateEmployment: moment.Moment | null;
  fEmplTerminationDate: moment.Moment | null;
  fEmplDateStart: moment.Moment | null;
  fEmplDateArrival: moment.Moment | null;
  fEmplTerminationReason: string | null;
  fEmplFixedTermReason: string | null;
  fEmplInfo: string;
  fEmplType: string | null;
};

export type ScheduleEmploymentPostData = {
  fEmplTrialPeriod: string;
  fEmplTrialPeriodEndDate: string;
  fEmplPlannedEndDate: string;
  fEmplLastDateEmployment: string;
  fEmplTerminationDate: string;
  fEmplTerminationReason: string;
  fEmplFixedTermReason: string;
  fEmplInfo: string;
  fEmplIsPending: boolean;
  fEmplType?: string;
  fEmplCurrency?: string;
  fEmplDateStart: string;
  fEmplDateArrival: string;
};

export const useEmployment = (
  employmentDataPending?: EmploymentData,
): UseEmploymentOutput => {
  const { getEnums, isLoading: enumsLoading } = useAllEnums();
  const { employeeEmploymentData } = useEmployeeEmploymentData();
  const { employee, isLoading: employeeLoading } = useEmployee();
  const employmentData = useMemo(
    () => employeeEmploymentData[0],
    [employeeEmploymentData],
  );
  const { t } = useTranslation();
  const { terminationReasons, fixedTermReasons, employmentTypeOptions } =
    useMemo(() => {
      return {
        terminationReasons: filterActiveEnums(
          getEnums(EnumListKey.EMPL_TERMINATION_REASON),
        ),
        fixedTermReasons: filterActiveEnums(
          getEnums(EnumListKey.EMPL_FIXED_TERM_REASON),
        ),
        employmentTypeOptions: getEnums(EnumListKey.EMPLOYMENT_TYPE),
      };
    }, [getEnums]);

  const trialPeriodOptions: Enum[] = [
    { code: 'P1M', name: `1 ${t('laMonth')}` },
    { code: 'P2M', name: `2 ${t('laMonth')}` },
    { code: 'P3M', name: `3 ${t('laMonth')}` },
    { code: 'P4M', name: `4 ${t('laMonth')}` },
    { code: 'P5M', name: `5 ${t('laMonth')}` },
    { code: 'P6M', name: `6 ${t('laMonth')}` },
  ];

  const mapData = (
    data: EmploymentFormValues,
    pending: boolean,
  ): ScheduleEmploymentPostData => ({
    ...data,
    fEmplType: data.fEmplType || '',
    fEmplCurrency: employmentData.fEmplCurrency,
    fEmplDateStart: moment(data.fEmplDateStart, dotFormat).format(dashFormat),
    fEmplDateArrival: moment(data.fEmplDateArrival, dotFormat).format(
      dashFormat,
    ),
    fEmplTrialPeriod: data.fEmplTrialPeriod || '',
    fEmplTrialPeriodEndDate: data.fEmplTrialPeriodEndDate
      ? moment(data.fEmplTrialPeriodEndDate, dotFormat).format(dashFormat)
      : '',
    fEmplPlannedEndDate: data.fEmplPlannedEndDate
      ? moment(data.fEmplPlannedEndDate, dotFormat).format(dashFormat)
      : '',
    fEmplLastDateEmployment: data.fEmplLastDateEmployment
      ? moment(data.fEmplLastDateEmployment, dotFormat).format(dashFormat)
      : '',
    fEmplTerminationDate: data.fEmplTerminationDate
      ? moment(data.fEmplTerminationDate, dotFormat).format(dashFormat)
      : '',
    fEmplTerminationReason: data.fEmplTerminationReason || '',
    fEmplFixedTermReason: data.fEmplFixedTermReason || '',
    fEmplInfo: data.fEmplInfo || '',
    fEmplIsPending: pending,
  });

  return {
    employmentData: employmentDataPending
      ? employmentDataPending
      : employmentData,
    terminationReasons,
    fixedTermReasons,
    trialPeriodOptions,
    employmentTypeOptions,
    loading: enumsLoading || employeeLoading,
    mapEmploymentPostData: mapData,
    hasWorkEmail: !!employee.empWorkContact.fEmpEmailWork,
  };
};
